<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <!-- Header -->
    <header class="relative">
      <!-- Navigation -->
      <nav class="absolute top-0 left-0 right-0 z-50">
        <div class="container mx-auto px-6 py-4">
          <div class="flex items-center justify-between">
            <!-- Logo -->
            <router-link to="/" class="flex items-center space-x-3 group">
              <div class="w-12 h-12 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/25 group-hover:scale-105 transition-transform">
                <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 14a6 6 0 1112 0H8z"/>
                </svg>
              </div>
              <span class="text-2xl font-bold text-white">OilBuddyAI</span>
            </router-link>

            <!-- Navigation Links -->
            <div class="hidden md:flex items-center space-x-8">
              <router-link to="/" class="text-slate-300 hover:text-white transition-colors duration-200">Home</router-link>
              <router-link to="/blog" class="text-slate-300 hover:text-white transition-colors duration-200">Blog</router-link>
              <router-link to="/support" class="text-slate-300 hover:text-white transition-colors duration-200">Support</router-link>
              <router-link to="/contact" class="text-slate-300 hover:text-white transition-colors duration-200">Contact</router-link>
              <router-link to="/roadmap" class="text-slate-300 hover:text-white transition-colors duration-200">Roadmap</router-link>
              <router-link to="/tokens" class="text-emerald-400 font-medium">Tokens</router-link>
              <a href="/#/login" class="btn-outline">Sign In</a>
              <router-link to="/register" class="btn-primary">Get Started</router-link>
            </div>

            <!-- Mobile Menu Button -->
            <button @click="toggleMobileMenu" class="md:hidden text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
          </div>

          <!-- Mobile Menu -->
          <div v-if="showMobileMenu" class="md:hidden mt-4 py-4 border-t border-slate-700">
            <div class="flex flex-col space-y-4">
              <router-link to="/" class="text-slate-300 hover:text-white transition-colors duration-200">Home</router-link>
              <router-link to="/blog" class="text-slate-300 hover:text-white transition-colors duration-200">Blog</router-link>
              <router-link to="/support" class="text-slate-300 hover:text-white transition-colors duration-200">Support</router-link>
              <router-link to="/contact" class="text-slate-300 hover:text-white transition-colors duration-200">Contact</router-link>
              <router-link to="/roadmap" class="text-slate-300 hover:text-white transition-colors duration-200">Roadmap</router-link>
              <router-link to="/tokens" class="text-emerald-400 font-medium">Tokens</router-link>
              <a href="/#/login" class="btn-outline">Sign In</a>
              <router-link to="/register" class="btn-primary">Get Started</router-link>
            </div>
          </div>
        </div>
      </nav>
    </header>

    <!-- Main Content -->
    <main class="pt-24 pb-16">
      <div class="container mx-auto px-6">
        <!-- Page Header -->
        <div class="text-center mb-16">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            AI Token Marketplace
          </div>
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Purchase AI Tokens
          </h1>
          <p class="text-xl text-slate-300 max-w-3xl mx-auto">
            Buy token packs for your AI agents. Choose from the latest OpenAI and Anthropic models with competitive pricing.
          </p>
        </div>

        <!-- Token Balance Section -->
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 mb-12">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-bold text-white">Your Token Balance</h2>
            <div class="flex items-center space-x-4">
              <div class="text-right">
                <div class="text-slate-400 text-sm">Total Tokens</div>
                <div class="text-2xl font-bold text-emerald-400">{{ formatNumber(userTokenBalance) }}</div>
              </div>
              <div class="text-right">
                <div class="text-slate-400 text-sm">Estimated Value</div>
                <div class="text-2xl font-bold text-blue-400">${{ formatCurrency(userTokenValue) }}</div>
              </div>
            </div>
          </div>
          
          <!-- Token Usage Chart -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="bg-slate-700/50 rounded-xl p-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-slate-300 text-sm">OpenAI Tokens</span>
                <span class="text-emerald-400 font-medium">{{ formatNumber(openAITokens) }}</span>
              </div>
              <div class="w-full bg-slate-600 rounded-full h-2">
                <div class="bg-emerald-500 h-2 rounded-full" :style="{ width: openAIUsagePercent + '%' }"></div>
              </div>
            </div>
            
            <div class="bg-slate-700/50 rounded-xl p-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-slate-300 text-sm">Anthropic Tokens</span>
                <span class="text-blue-400 font-medium">{{ formatNumber(anthropicTokens) }}</span>
              </div>
              <div class="w-full bg-slate-600 rounded-full h-2">
                <div class="bg-blue-500 h-2 rounded-full" :style="{ width: anthropicUsagePercent + '%' }"></div>
              </div>
            </div>
            
            <div class="bg-slate-700/50 rounded-xl p-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-slate-300 text-sm">Usage This Month</span>
                <span class="text-purple-400 font-medium">{{ formatNumber(monthlyUsage) }}</span>
              </div>
              <div class="w-full bg-slate-600 rounded-full h-2">
                <div class="bg-purple-500 h-2 rounded-full" :style="{ width: monthlyUsagePercent + '%' }"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Token Packs Section -->
        <div class="mb-16">
          <h2 class="text-3xl font-bold text-white mb-8 text-center">Token Packs</h2>
          
          <!-- Provider Tabs -->
          <div class="flex justify-center mb-8">
            <div class="bg-slate-800/50 rounded-xl p-1">
              <button 
                @click="selectedProvider = 'openai'"
                :class="selectedProvider === 'openai' ? 'bg-emerald-500 text-white' : 'text-slate-300 hover:text-white'"
                class="px-6 py-3 rounded-lg font-medium transition-all duration-200"
              >
                OpenAI Models
              </button>
              <button 
                @click="selectedProvider = 'anthropic'"
                :class="selectedProvider === 'anthropic' ? 'bg-emerald-500 text-white' : 'text-slate-300 hover:text-white'"
                class="px-6 py-3 rounded-lg font-medium transition-all duration-200"
              >
                Anthropic Models
              </button>
            </div>
          </div>

          <!-- Token Packs Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div 
              v-for="pack in filteredTokenPacks" 
              :key="pack.id"
              class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:border-emerald-500/30 transition-all duration-300 hover:transform hover:scale-105"
            >
              <!-- Pack Header -->
              <div class="text-center mb-6">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" :class="pack.provider === 'openai' ? 'bg-emerald-500/20' : 'bg-blue-500/20'">
                  <svg class="w-8 h-8" :class="pack.provider === 'openai' ? 'text-emerald-400' : 'text-blue-400'" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <h3 class="text-xl font-bold text-white mb-2">{{ pack.name }}</h3>
                <p class="text-slate-400 text-sm">{{ pack.description }}</p>
              </div>

              <!-- Token Amount -->
              <div class="text-center mb-6">
                <div class="text-3xl font-bold text-emerald-400 mb-2">{{ formatNumber(pack.tokens) }}</div>
                <div class="text-slate-400 text-sm">tokens</div>
              </div>

              <!-- Pricing -->
              <div class="text-center mb-6">
                <div class="text-2xl font-bold text-white mb-1">${{ pack.price }}</div>
                <div class="text-slate-400 text-sm">per pack</div>
                <div class="text-emerald-400 text-sm mt-2">${{ pack.pricePerMillion }} per million tokens</div>
              </div>

              <!-- Features -->
              <div class="space-y-3 mb-8">
                <div class="flex items-center text-sm">
                  <svg class="w-4 h-4 text-emerald-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                  <span class="text-slate-300">{{ pack.model }}</span>
                </div>
                <div class="flex items-center text-sm">
                  <svg class="w-4 h-4 text-emerald-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                  <span class="text-slate-300">Input & Output tokens</span>
                </div>
                <div class="flex items-center text-sm">
                  <svg class="w-4 h-4 text-emerald-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                  <span class="text-slate-300">No expiration</span>
                </div>
                <div v-if="pack.promptCache" class="flex items-center text-sm">
                  <svg class="w-4 h-4 text-emerald-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                  <span class="text-slate-300">Prompt caching included</span>
                </div>
              </div>

              <!-- Purchase Button -->
              <button 
                @click="purchaseTokens(pack)"
                :disabled="isProcessing"
                class="w-full btn-primary-modern disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="isProcessing" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isProcessing ? 'Processing...' : 'Purchase Pack' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Pricing Comparison Table -->
        <div class="mb-16">
          <h2 class="text-3xl font-bold text-white mb-8 text-center">Pricing Comparison</h2>
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="bg-slate-700/50">
                    <th class="px-6 py-4 text-left text-sm font-medium text-slate-300">Provider</th>
                    <th class="px-6 py-4 text-left text-sm font-medium text-slate-300">Model</th>
                    <th class="px-6 py-4 text-left text-sm font-medium text-slate-300">Input / MTok</th>
                    <th class="px-6 py-4 text-left text-sm font-medium text-slate-300">Output / MTok</th>
                    <th class="px-6 py-4 text-left text-sm font-medium text-slate-300">Prompt-Cache Write</th>
                    <th class="px-6 py-4 text-left text-sm font-medium text-slate-300">Read Cache</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-700/50">
                  <tr v-for="model in pricingTable" :key="model.id" class="hover:bg-slate-700/30 transition-colors">
                    <td class="px-6 py-4 text-sm text-white font-medium">{{ model.provider }}</td>
                    <td class="px-6 py-4 text-sm text-slate-300">{{ model.name }}</td>
                    <td class="px-6 py-4 text-sm text-emerald-400">${{ model.input }}</td>
                    <td class="px-6 py-4 text-sm text-blue-400">${{ model.output }}</td>
                    <td class="px-6 py-4 text-sm text-purple-400">{{ model.writeCache || '—' }}</td>
                    <td class="px-6 py-4 text-sm text-orange-400">{{ model.readCache || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Usage Calculator -->
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
          <h2 class="text-3xl font-bold text-white mb-6 text-center">Token Usage Calculator</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Select Model</label>
              <select 
                v-model="calculatorModel"
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
              >
                <option value="">Select a model</option>
                <option v-for="model in allModels" :key="model.id" :value="model.id">{{ model.name }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Input Tokens</label>
              <input 
                v-model="inputTokens"
                type="number"
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                placeholder="Enter input tokens"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Output Tokens</label>
              <input 
                v-model="outputTokens"
                type="number"
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                placeholder="Enter output tokens"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Prompt Cache Hits</label>
              <input 
                v-model="cacheHits"
                type="number"
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                placeholder="Enter cache hits (if applicable)"
              >
            </div>
          </div>
          
          <!-- Calculation Results -->
          <div v-if="calculatorModel && (inputTokens || outputTokens)" class="mt-8 p-6 bg-slate-700/30 rounded-xl">
            <h3 class="text-lg font-semibold text-white mb-4">Cost Calculation</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div class="text-center">
                <div class="text-slate-400 text-sm mb-1">Input Cost</div>
                <div class="text-2xl font-bold text-emerald-400">${{ formatCurrency(inputCost) }}</div>
              </div>
              <div class="text-center">
                <div class="text-slate-400 text-sm mb-1">Output Cost</div>
                <div class="text-2xl font-bold text-blue-400">${{ formatCurrency(outputCost) }}</div>
              </div>
              <div class="text-center">
                <div class="text-slate-400 text-sm mb-1">Total Cost</div>
                <div class="text-2xl font-bold text-white">${{ formatCurrency(totalCost) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-slate-900/50 border-t border-slate-700/50">
      <div class="container mx-auto px-6 py-12">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- Company Info -->
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center space-x-3 mb-4">
              <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-10 h-10 rounded-xl shadow-lg shadow-emerald-500/25" />
              <span class="text-xl font-bold text-white">OilBuddyAI</span>
            </div>
            <p class="text-slate-400 mb-6 max-w-md">
              Revolutionizing the oil and gas industry with AI-powered solutions for contractors, companies, and professionals.
            </p>
            <div class="flex space-x-4">
              <a href="#" class="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="#" class="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          <!-- Quick Links -->
          <div>
            <h3 class="text-lg font-semibold text-white mb-4">Quick Links</h3>
            <ul class="space-y-2">
              <li><router-link to="/" class="text-slate-400 hover:text-white transition-colors duration-200">Home</router-link></li>
              <li><router-link to="/blog" class="text-slate-400 hover:text-white transition-colors duration-200">Blog</router-link></li>
              <li><router-link to="/support" class="text-slate-400 hover:text-white transition-colors duration-200">Support</router-link></li>
              <li><router-link to="/contact" class="text-slate-400 hover:text-white transition-colors duration-200">Contact</router-link></li>
              <li><router-link to="/roadmap" class="text-slate-400 hover:text-white transition-colors duration-200">Roadmap</router-link></li>
              <li><router-link to="/tokens" class="text-slate-400 hover:text-white transition-colors duration-200">Tokens</router-link></li>
            </ul>
          </div>

          <!-- Contact Info -->
          <div>
            <h3 class="text-lg font-semibold text-white mb-4">Contact</h3>
            <ul class="space-y-2">
              <li><a href="mailto:contact@oilbuddy.ai" class="text-slate-400 hover:text-white transition-colors duration-200">contact@oilbuddy.ai</a></li>
              <li><a href="tel:+1-701-419-0006" class="text-slate-400 hover:text-white transition-colors duration-200">+1 (701) 419-0006</a></li>
              <li class="text-slate-400">117 5th AVE SW<br>Minto ND 58701</li>
            </ul>
          </div>
        </div>

        <div class="border-t border-slate-700/50 mt-8 pt-8 text-center">
          <p class="text-slate-400">&copy; 2024 OilBuddyAI. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

// Mobile menu state
const showMobileMenu = ref(false)

// Purchase state
const isProcessing = ref(false)

// Provider selection
const selectedProvider = ref('openai')

// User token balance (mock data)
const userTokenBalance = ref(2500000) // 2.5M tokens
const userTokenValue = ref(125.50) // $125.50
const openAITokens = ref(1500000) // 1.5M tokens
const anthropicTokens = ref(1000000) // 1M tokens
const monthlyUsage = ref(500000) // 500K tokens

// Token usage percentages
const openAIUsagePercent = computed(() => (openAITokens.value / userTokenBalance.value) * 100)
const anthropicUsagePercent = computed(() => (anthropicTokens.value / userTokenBalance.value) * 100)
const monthlyUsagePercent = computed(() => (monthlyUsage.value / userTokenBalance.value) * 100)

// Calculator state
const calculatorModel = ref('')
const inputTokens = ref(0)
const outputTokens = ref(0)
const cacheHits = ref(0)

// Token packs data
const tokenPacks = ref([
  // OpenAI Packs
  {
    id: 'openai-gpt4o-mini',
    provider: 'openai',
    name: 'GPT-4o Mini Pack',
    description: 'Most cost-effective for high-volume usage',
    model: 'GPT-4o mini',
    tokens: 1000000,
    price: 0.75,
    pricePerMillion: 0.75,
    promptCache: false
  },
  {
    id: 'openai-gpt4o',
    provider: 'openai',
    name: 'GPT-4o Standard Pack',
    description: 'Balanced performance and cost',
    model: 'GPT-4o',
    tokens: 1000000,
    price: 12.50,
    pricePerMillion: 12.50,
    promptCache: false
  },
  {
    id: 'openai-gpt45',
    provider: 'openai',
    name: 'GPT-4.5 Premium Pack',
    description: 'Latest model with advanced reasoning',
    model: 'GPT-4.5',
    tokens: 1000000,
    price: 225.00,
    pricePerMillion: 225.00,
    promptCache: false
  },
  {
    id: 'openai-o1-pro',
    provider: 'openai',
    name: 'o1-Pro Enterprise Pack',
    description: 'Cutting-edge reasoning and performance',
    model: 'o1-Pro',
    tokens: 1000000,
    price: 750.00,
    pricePerMillion: 750.00,
    promptCache: false
  },
  
  // Anthropic Packs
  {
    id: 'anthropic-haiku',
    provider: 'anthropic',
    name: 'Claude Haiku Pack',
    description: 'Fastest and most cost-efficient',
    model: 'Claude Haiku 3.5',
    tokens: 1000000,
    price: 4.80,
    pricePerMillion: 4.80,
    promptCache: true
  },
  {
    id: 'anthropic-sonnet',
    provider: 'anthropic',
    name: 'Claude Sonnet Pack',
    description: 'Balanced performance and cost',
    model: 'Claude Sonnet 4',
    tokens: 1000000,
    price: 18.00,
    pricePerMillion: 18.00,
    promptCache: true
  },
  {
    id: 'anthropic-opus',
    provider: 'anthropic',
    name: 'Claude Opus Pack',
    description: 'Highest capability and reasoning',
    model: 'Claude Opus 4',
    tokens: 1000000,
    price: 90.00,
    pricePerMillion: 90.00,
    promptCache: true
  }
])

// Pricing table data
const pricingTable = ref([
  {
    id: 'gpt45',
    provider: 'OpenAI',
    name: 'GPT-4.5',
    input: '75',
    output: '150',
    writeCache: '—',
    readCache: '—'
  },
  {
    id: 'o1-pro',
    provider: 'OpenAI',
    name: 'o1-Pro',
    input: '150',
    output: '600',
    writeCache: '—',
    readCache: '—'
  },
  {
    id: 'gpt4o',
    provider: 'OpenAI',
    name: 'GPT-4o',
    input: '2.50',
    output: '10',
    writeCache: '—',
    readCache: '—'
  },
  {
    id: 'gpt4o-mini',
    provider: 'OpenAI',
    name: 'GPT-4o mini',
    input: '0.15',
    output: '0.60',
    writeCache: '—',
    readCache: '—'
  },
  {
    id: 'opus4',
    provider: 'Anthropic',
    name: 'Claude Opus 4',
    input: '15',
    output: '75',
    writeCache: '18.75',
    readCache: '1.50'
  },
  {
    id: 'sonnet4',
    provider: 'Anthropic',
    name: 'Claude Sonnet 4',
    input: '3',
    output: '15',
    writeCache: '3.75',
    readCache: '0.30'
  },
  {
    id: 'haiku35',
    provider: 'Anthropic',
    name: 'Claude Haiku 3.5',
    input: '0.80',
    output: '4',
    writeCache: '1.00',
    readCache: '0.08'
  }
])

// All models for calculator
const allModels = computed(() => [
  ...pricingTable.value.map(model => ({
    id: model.id,
    name: `${model.provider} - ${model.name}`,
    ...model
  }))
])

// Computed properties
const filteredTokenPacks = computed(() => 
  tokenPacks.value.filter(pack => pack.provider === selectedProvider.value)
)

const selectedModelData = computed(() => 
  pricingTable.value.find(model => model.id === calculatorModel.value)
)

const inputCost = computed(() => {
  if (!selectedModelData.value || !inputTokens.value) return 0
  return (inputTokens.value / 1000000) * parseFloat(selectedModelData.value.input)
})

const outputCost = computed(() => {
  if (!selectedModelData.value || !outputTokens.value) return 0
  return (outputTokens.value / 1000000) * parseFloat(selectedModelData.value.output)
})

const cacheCost = computed(() => {
  if (!selectedModelData.value || !cacheHits.value || !selectedModelData.value.readCache) return 0
  return (cacheHits.value / 1000000) * parseFloat(selectedModelData.value.readCache)
})

const totalCost = computed(() => inputCost.value + outputCost.value + cacheCost.value)

// Methods
const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
}

const formatNumber = (num: number) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toLocaleString()
}

const formatCurrency = (num: number) => {
  return num.toFixed(2)
}

const purchaseTokens = async (pack: any) => {
  isProcessing.value = true
  
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // Update user balance
    userTokenBalance.value += pack.tokens
    if (pack.provider === 'openai') {
      openAITokens.value += pack.tokens
    } else {
      anthropicTokens.value += pack.tokens
    }
    
    alert(`Successfully purchased ${formatNumber(pack.tokens)} tokens for $${pack.price}!`)
  } catch (error) {
    alert('There was an error processing your purchase. Please try again.')
  } finally {
    isProcessing.value = false
  }
}
</script>

<style scoped>
.btn-primary-modern {
  @apply inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 transform hover:scale-105;
}

.btn-outline {
  @apply inline-flex items-center px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700/50 hover:text-white transition-colors duration-200;
}

.btn-primary {
  @apply inline-flex items-center px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200;
}
</style>
